import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Scissors, Store, UserRound, Sparkles, Waves, BadgeCheck, Flower2, Briefcase } from "lucide-react";
import { UNIVERSES } from "@/data/universes";
import { CtaButtons } from "@/components/home/shared";

export const HomeHero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden bg-[var(--oz-dark)] text-white">
    <img src="/img/home/pro-hero.webp" alt="Salon de coiffure professionnel réunissant coiffure afro, coiffure européenne et barbier"
      width="1264" height="848" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover object-[70%_center] opacity-60" />
    <div className="absolute inset-0 bg-gradient-to-r from-[var(--oz-dark)] via-[var(--oz-dark)]/85 to-transparent" />
    <div className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-20 sm:py-28 lg:py-36">
      <div className="max-w-2xl oz-fade-up">
        <p data-testid="home-hero-kicker" className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-[var(--oz-gold)]">
          Ozentya — la plateforme dédiée aux professionnels de la coiffure
        </p>
        <h1 data-testid="home-hero-title" className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
          Tous vos essentiels professionnels de coiffure réunis sur Ozentya.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-white/80 leading-relaxed">
          Coiffure afro, coiffure européenne et barber shop : découvrez une plateforme pensée pour les professionnels de la coiffure, leurs besoins et leur activité au quotidien.
        </p>
        <div className="mt-9"><CtaButtons dark testid="home-hero" /></div>
        <p className="mt-5 text-sm text-white/60">Une plateforme conçue pour accompagner les professionnels de la coiffure partout en France.</p>
      </div>
    </div>
  </section>
);

export const HomeUniverses = () => (
  <section data-testid="home-universes" className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-20">
    <h2 className="sr-only">Nos trois univers professionnels</h2>
    <div className="grid gap-5 md:grid-cols-3">
      {UNIVERSES.map((u, i) => (
        <Link key={u.key} to={u.path} data-testid={`home-universe-${u.key}`} style={{ animationDelay: `${i * 90}ms` }}
          className="oz-fade-up group relative rounded-3xl overflow-hidden min-h-[380px] flex items-end bg-[var(--oz-dark)]">
          <img src={u.image} alt={u.alt} loading="lazy" width="800" height="800" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
          <div className="relative p-6 text-white">
            <h3 className="font-serif text-2xl">{u.title}</h3>
            <p className="mt-2 text-sm text-white/80 leading-relaxed">{u.teaser}</p>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--oz-gold)]">Découvrir <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

const AUDIENCE = [
  [Store, "Salons de coiffure afro", "Soins hydratants, démêlants et cuir chevelu pour vos prestations au bac et vos coiffures protectrices."],
  [Scissors, "Salons de coiffure européenne", "Shampoings, après-shampooing et masques adaptés aux cheveux fins, raides, ondulés ou abîmés."],
  [BadgeCheck, "Barbershops et barbiers indépendants", "Huiles à barbe, edge control et soins du cuir chevelu pour vos finitions."],
  [UserRound, "Coiffeurs indépendants", "À domicile ou en fauteuil loué : commandez au conditionnement qui vous convient."],
  [Waves, "Spécialistes des cheveux texturés", "Locks, tresses, naturel : une gamme pensée pour les cheveux crépus, frisés et bouclés."],
  [Sparkles, "Boutiques de cosmétiques", "Une gamme capillaire, barbe et visage à revendre en rayon, commandée par conditionnement à prix HT."],
  [Flower2, "Salons d'esthétique et instituts", "Le Sérum Anti-Tâches Lumina V.G pour vos soins visage, et des soins capillaires à proposer à votre clientèle."],
  [Briefcase, "Autres professionnels éligibles", "Revendeurs et entreprises de la beauté immatriculés peuvent aussi ouvrir un compte."],
];

export const HomeAudience = () => (
  <section data-testid="home-audience" className="bg-[var(--oz-bg-2)]">
    <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-16 sm:py-24 grid lg:grid-cols-[1fr_1.6fr] gap-12">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)]">Pensé pour les professionnels</p>
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl leading-tight">Votre partenaire au quotidien, quel que soit votre univers de coiffure.</h2>
        <p className="mt-5 text-[var(--oz-muted)] leading-relaxed">
          Ozentya accompagne les professionnels de la coiffure dans leur recherche de produits et de solutions adaptés à leur métier. Retrouvez un espace pensé pour vos besoins professionnels et votre activité.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {AUDIENCE.map(([Icon, t, d]) => (
          <div key={t} className="rounded-2xl bg-white p-5 border border-[var(--oz-border)]">
            <Icon className="w-5 h-5 text-[var(--oz-gold)]" />
            <h3 className="mt-3 font-semibold">{t}</h3>
            <p className="mt-1 text-sm text-[var(--oz-muted)] leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);
